import { Fragment, use } from "react";

import { mergeClassNames } from "@bokio/utils/classes";

import { CardRadio } from "../CardRadio/CardRadio";
import { Validation } from "../Form";
import Field from "../Form/Field/Field.internals";
import { FormContext } from "../Form/Form";
import { Label } from "../Form/LabelFor/Label";

import type { CardRadioSubItem } from "../CardRadio/CardRadio";
import type { UnTypedInputFieldProps } from "../Form/InputField";
import type { ReactNode } from "react";

import * as styles from "./cardRadioGroup.scss";

export interface CardRadioOption<T extends string = string> {
	/** Sub-items to display in the card (title + optional description pairs) */
	subItems: CardRadioSubItem[];
	/** Value of this option */
	value: T;
	/** Whether this option is disabled */
	disabled?: boolean;
	/** Test ID for the label element */
	testId?: string;
	/** Test ID for the input element */
	inputTestId?: string;
}

export interface CardRadioGroupProps<T extends string = string> extends Omit<
	UnTypedInputFieldProps,
	"value" | "onChange"
> {
	/** Unique name for the radio group */
	name: string;
	/** Label displayed above the radio group */
	label?: ReactNode;
	/** Options to display */
	options: CardRadioOption<T>[];
	/** Currently selected value */
	value?: T;
	/** Callback fired when selection changes */
	onChange?: (value: T) => void;
	/** Disables all options */
	disabled?: boolean;
	/** Layout direction of the options */
	layout?: "vertical" | "horizontal";
}

export const CardRadioGroup = <T extends string = string>({
	name,
	label,
	options,
	value,
	onChange,
	disabled,
	layout = "horizontal",
	wrapperClassName,
	errors,
	testId,
}: CardRadioGroupProps<T>) => {
	const { disableAllFields } = use(FormContext);
	const isHorizontal = layout === "horizontal";

	return (
		<Field className={wrapperClassName} testId={testId}>
			{label && <Label label={label} className={styles.groupLabel} />}
			<div className={mergeClassNames(isHorizontal && styles.optionsHorizontal, styles.group)}>
				{options.map(option => {
					const itemDisabled = disableAllFields || disabled || option.disabled;
					return (
						<Fragment key={option.value}>
							<CardRadio
								name={name}
								value={option.value}
								checked={value === option.value}
								disabled={itemDisabled}
								onChange={onChange as (value: string) => void}
								subItems={option.subItems}
								testId={option.testId}
								inputTestId={option.inputTestId}
							/>
						</Fragment>
					);
				})}
			</div>
			<Validation testId={`Validation_Inline_${testId}`} errors={errors && errors.errors} />
		</Field>
	);
};
